// learmo-editor.component.ts
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  forwardRef,
  OnChanges,
  SimpleChanges,
  NgZone,
  HostListener,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MarkdownService } from 'ngx-markdown';
import Quill from 'quill';
import QuillMarkdown from 'quilljs-markdown';
import QuillTableBetter from 'quill-table-better';

// Register Modules
Quill.register({ 'modules/table-better': QuillTableBetter }, true);

// Google Fonts Configuration
const Font = Quill.import('formats/font') as any;
Font.whitelist = [
  'system', // Default system font (Roboto)
  'inter', // Modern sans-serif
  'roboto', // Clean, geometric
  'open-sans', // Friendly, readable
  'lato', // Elegant sans-serif
  'montserrat', // Bold, modern
  'poppins', // Rounded, geometric
  'source-sans-pro', // Professional
  'playfair-display', // Elegant serif
  'merriweather', // Readable serif
  'lora', // Calligraphic serif
  'crimson-text', // Classic serif
  'pt-serif', // Transitional serif
  'fira-code', // Monospace for code
  'jetbrains-mono', // Modern monospace
  'source-code-pro', // Adobe monospace
  'dancing-script', // Handwriting
  'pacifico', // Fun script
  'caveat', // Natural handwriting
];
Quill.register(Font, true);

// CRITICAL: Configure Quill to preserve ALL classes and inline styles
const Inline = Quill.import('blots/inline') as any;
const Block = Quill.import('blots/block') as any;

// Create custom blot that preserves classes
class PreserveInline extends Inline {
  static blotName = 'preserve-inline';
  static tagName = 'SPAN';

  static formats(domNode: HTMLElement) {
    const formats: any = {};
    if (domNode.hasAttribute('class')) {
      formats.class = domNode.getAttribute('class');
    }
    if (domNode.hasAttribute('style')) {
      formats.style = domNode.getAttribute('style');
    }
    return formats;
  }

  format(name: string, value: any) {
    if (name === 'class' && value) {
      this.domNode.setAttribute('class', value);
    } else if (name === 'style' && value) {
      this.domNode.setAttribute('style', value);
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(PreserveInline, true);

// Custom Blot for Variables {{variable_name}}
const Embed = Quill.import('blots/embed') as any;
class VariableBlot extends Embed {
  static blotName = 'variable';
  static tagName = 'span';
  static className = 'ql-variable';
  static variableMap: Record<string, string> = {};
  static showValues: boolean = false;

  static create(name: string) {
    const node = super.create(name);
    const value = this.variableMap[name] || name;
    node.innerText = this.showValues ? value : `{{${name}}}`;
    node.setAttribute('data-value', name);
    node.setAttribute('title', `Variable: ${name}\nValue: ${value}`);
    node.setAttribute('contenteditable', 'false');
    return node;
  }

  static value(domNode: HTMLElement) {
    return domNode.getAttribute('data-value');
  }
}
Quill.register(VariableBlot);

export interface EditorConfig {
  placeholder?: string;
  readonly?: boolean;
  containerClass?: string;
  minHeight?: string;
}

export interface ActiveVariable {
  name: string;
  value: string;
  label: string;
  top: number;
  left: number;
  node: HTMLElement;
}

@Component({
  selector: 'app-learmo-editor',
  templateUrl: './learmo-editor.component.html',
  styleUrls: ['./learmo-editor.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LearmoEditorComponent),
      multi: true,
    },
  ],
})
export class LearmoEditorComponent
  implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Input() config: Partial<EditorConfig> = {};
  @Input() placeholder: string = 'Start typing...';
  @Input() disabled: boolean = false;
  @Input() content: string = '';
  @Input() variables: { name: string; value: string; label?: string }[] = [];

  @Output() contentChange = new EventEmitter<string>();
  @Output() editorReady = new EventEmitter<Quill>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  @ViewChild('editorContainer', { static: false })
  editorContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarTarget', { read: ElementRef }) toolbarTarget!: ElementRef;
  @ViewChild('editorShell', { static: false }) editorShellRef!: ElementRef;
  @ViewChild('variablePopover', { static: false })
  variablePopoverRef!: ElementRef;
  @ViewChild('variableDropdown', { static: false })
  variableDropdownRef!: ElementRef;

  public quill!: Quill;
  public isFocused: boolean = false;
  public isFullscreen: boolean = false;
  public showVariableDropdown: boolean = false;
  public showValues: boolean = false;
  public activeVariable: ActiveVariable | null = null;
  public dropdownPosition = { top: 0, left: 0 };

  private markdownModule?: any;
  private isInternalChange = false;
  private onChange = (value: string) => {};
  private onTouched = () => {};
  private lastEmittedContent = '';

  get activeConfig(): EditorConfig {
    return {
      placeholder: this.placeholder,
      readonly: this.disabled,
      ...this.config,
    };
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private mdService: MarkdownService,
    private ngZone: NgZone,
    private host: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    this.initializeQuill();
    this.teleportFloatingElements();
  }

  private teleportFloatingElements(): void {
    // Wait for elements to be available
    setTimeout(() => {
      if (this.variablePopoverRef) {
        this.renderer.appendChild(
          document.body,
          this.variablePopoverRef.nativeElement,
        );
      }
      if (this.variableDropdownRef) {
        this.renderer.appendChild(
          document.body,
          this.variableDropdownRef.nativeElement,
        );
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only handle content changes when editor is not focused
    if (changes['content'] && !changes['content'].firstChange && this.quill) {
      const newValue = changes['content'].currentValue || '';

      // Don't update if user is actively typing
      if (this.isFocused) {
        return;
      }

      const currentContent = this.getHTMLContent();

      // Normalize both values for comparison
      const normalizedNew = this.normalizeHTML(newValue);
      const normalizedCurrent = this.normalizeHTML(currentContent);

      if (normalizedNew !== normalizedCurrent) {
        this.writeValue(newValue);
      }
    }

    if (changes['disabled'] && this.quill) {
      this.quill.enable(!this.disabled);
    }

    if (changes['variables']) {
      this.showVariableDropdown = false;
      this.cdr.markForCheck();
      this.updateVariableMap();
    }
  }

  public toggleShowValues(): void {
    this.showValues = !this.showValues;
    VariableBlot.showValues = this.showValues;
    this.updateVariableDisplay();
    this.cdr.markForCheck();
  }

  private updateVariableDisplay(): void {
    const map = VariableBlot.variableMap;
    const blots = this.host.nativeElement.querySelectorAll('.ql-variable');
    blots.forEach((el: HTMLElement) => {
      const name = el.getAttribute('data-value');
      if (name) {
        const value = map[name] || name;
        el.innerText = this.showValues ? value : `{{${name}}}`;
      }
    });
  }

  private updateVariableMap(): void {
    const map: Record<string, string> = {};
    this.variables.forEach((v) => (map[v.name] = v.value));
    VariableBlot.variableMap = map;
    VariableBlot.showValues = this.showValues; // Ensure static state matches

    // If editor exists, we might want to update existing blots' titles
    if (this.quill) {
      this.updateVariableDisplay();
    }
  }

  public insertVariable(name: string): void {
    if (!this.quill) return;
    const range = this.quill.getSelection(true);
    this.quill.insertEmbed(range.index, 'variable', name, 'user');
    this.quill.setSelection(range.index + 1, 0, 'user');
  }

  ngOnDestroy(): void {
    // If we're in fullscreen, we must move the element back to the host before destruction
    // or manually remove it from the body to prevent orphans.
    if (this.isFullscreen && this.editorShellRef) {
      this.toggleFullscreen(false);
    }

    this.markdownModule?.destroy();
    if (this.quill) {
      this.quill.off('text-change');
      this.quill.off('selection-change');
    }

    // Clean up teleported elements
    if (this.variablePopoverRef) {
      this.variablePopoverRef.nativeElement.remove();
    }
    if (this.variableDropdownRef) {
      this.variableDropdownRef.nativeElement.remove();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if (this.isFullscreen) {
      this.toggleFullscreen(false);
    }
    this.showVariableDropdown = false;
  }
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const variableNode = target.closest('.ql-variable') as HTMLElement;
    const isPickerClick = target.closest('.variable-picker-btn');
    const isDropdownClick = target.closest('.variable-dropdown');
    const isPopoverClick = target.closest('.variable-popover');

    // If clicking inside the popover or dropdown, do nothing (keep it open)
    if (isPopoverClick || isDropdownClick) {
      return;
    }

    // If clicking a variable, toggle it
    if (variableNode && this.host.nativeElement.contains(variableNode)) {
      const name = variableNode.getAttribute('data-value') || '';
      // If clicking the SAME variable that is already active, don't re-open/toggle loop
      if (this.activeVariable?.node === variableNode) {
        return;
      }
      this.openVariablePopover(event, name, variableNode);
      return;
    }

    // Close logic:
    // 1. Clicked outside editor entirely
    // 2. Clicked inside editor but NOT on a variable or picker (and not on popover/dropdown as per check above)
    if (
      !this.host.nativeElement.contains(target) ||
      (!variableNode && !isPickerClick)
    ) {
      this.clearActiveVariable();
      this.showVariableDropdown = false;
      this.cdr.markForCheck();
    }
  }

  private clearActiveVariable(): void {
    if (this.activeVariable?.node) {
      this.activeVariable.node.classList.remove('active');
    }
    this.activeVariable = null;
  }

  private openVariablePopover(
    event: MouseEvent,
    name: string,
    node: HTMLElement,
  ): void {
    if (this.activeVariable?.node) {
      this.activeVariable.node.classList.remove('active');
    }

    const rect = node.getBoundingClientRect();
    const containerRect = this.host.nativeElement.getBoundingClientRect();

    const varData = this.variables.find((v) => v.name === name);

    node.classList.add('active');

    // For position: fixed, we use viewport coordinates directly
    // rect.left is already relative to viewport
    const center = rect.left + rect.width / 2;

    // Viewport dimensions
    const viewportWidth = window.innerWidth;

    // Safety padding
    const safetyMargin = 160;
    const minLeft = safetyMargin;
    const maxLeft = viewportWidth - safetyMargin;

    this.activeVariable = {
      name,
      value: varData?.value || name,
      label: this.getLabelForVar(name),
      top: rect.bottom + 8, // Direct viewport top
      left: Math.max(minLeft, Math.min(center, maxLeft)),
      node: node,
    };

    this.cdr.markForCheck();
  }

  public removeVariable(): void {
    if (!this.activeVariable || !this.quill) return;
    const blot = Quill.find(this.activeVariable.node) as any;
    if (blot && typeof blot.deleteAt === 'function') {
      const index = this.quill.getIndex(blot);
      this.quill.deleteText(index, 1, 'user');
    }
    this.activeVariable = null;
    this.cdr.markForCheck();
  }

  public detachVariable(): void {
    if (!this.activeVariable || !this.quill) return;
    const blot = Quill.find(this.activeVariable.node) as any;
    if (blot && typeof blot.deleteAt === 'function') {
      const index = this.quill.getIndex(blot);
      const value = this.activeVariable.value;
      this.quill.deleteText(index, 1, 'user');
      this.quill.insertText(index, value, 'user');
    }
    this.activeVariable = null;
    this.cdr.markForCheck();
  }

  getLabelForVar(name: string): string {
    const labels: Record<string, string> = {
      siteName: 'Site Name',
      siteUrl: 'Site URL',
      lastUpdated: 'Last Updated Date',
      legalAddress: 'Business Address',
      supportEmail: 'Support Email',
      refundPeriod: 'Refund Period',
      legalVenue: 'Legal Venue (Jurisdiction)',
      companyName: 'Company Name',
      contactEmail: 'Contact Email',
      siteDescription: 'Site Description',
      replyEmail: 'Reply-To Email',
    };
    return labels[name] || name;
  }

  public toggleVariableDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showVariableDropdown = !this.showVariableDropdown;

    if (this.showVariableDropdown) {
      const button = event.currentTarget as HTMLElement;
      const rect = button.getBoundingClientRect();

      // Position below the button
      this.dropdownPosition = {
        top: rect.bottom + 8,
        left: rect.left,
      };

      // Ensure it doesn't go off-screen
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 220; // min-width from CSS
      if (this.dropdownPosition.left + dropdownWidth > viewportWidth) {
        this.dropdownPosition.left = viewportWidth - dropdownWidth - 16;
      }
    }

    this.cdr.markForCheck();
  }

  public onVariableSelect(name: string): void {
    this.insertVariable(name);
    this.showVariableDropdown = false;
    this.cdr.markForCheck();
  }

  private initializeQuill(): void {
    if (!this.editorContainerRef) return;

    this.ngZone.runOutsideAngular(() => {
      this.quill = new Quill(this.editorContainerRef.nativeElement, {
        theme: 'snow',
        placeholder: this.activeConfig.placeholder,
        readOnly: this.activeConfig.readonly,
        modules: {
          clipboard: {
            matchVisual: false,
            matchers: [
              // Preserve all inline styles and classes
              [
                '*',
                (node: any, delta: any) => {
                  return delta;
                },
              ],
            ],
          },
          toolbar: {
            container: [
              [{ font: Font.whitelist }, { header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ color: [] }, { background: [] }],
              [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
              ['link', 'code-block', 'blockquote'],
              ['table-better'],
            ],
            handlers: {
              image: this.imageHandler.bind(this),
            },
          },
          'table-better': {
            language: 'en_US',
            toolbarTable: true,
            menus: [
              'column',
              'row',
              'merge',
              'table',
              'cell',
              'wrap',
              'delete',
            ],
          },
        },
      });

      // Initialize Markdown support
      this.markdownModule = new QuillMarkdown(this.quill, {});

      // Text change handler
      this.quill.on('text-change', (delta, oldDelta, source) => {
        if (source === 'user') {
          this.ngZone.run(() => {
            this.handleVariableDetection(delta);
            this.handleTextChange();
            this.clearActiveVariable();
          });
        }
      });

      // Selection change handler
      this.quill.on('selection-change', (range) => {
        this.ngZone.run(() => {
          if (!range) {
            this.onTouched();
            this.isFocused = false;
            this.blur.emit();
          } else {
            this.isFocused = true;
            this.focus.emit();
          }
          this.cdr.markForCheck();
        });
      });

      // Inject custom toolbar buttons
      this.injectCustomToolbarButtons();
    });

    if (this.content) {
      this.writeValue(this.content);
    }

    this.editorReady.emit(this.quill);
  }

  private imageHandler(): void {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const range = this.quill.getSelection(true);
          const imageUrl = e.target?.result as string;

          this.quill.insertEmbed(range.index, 'image', imageUrl);
          this.quill.setSelection(range.index + 1, 0);

          this.ngZone.run(() => {
            this.handleTextChange();
          });
        };
        reader.readAsDataURL(file);
      }
    };

    input.click();
  }

  private handleVariableDetection(delta: any): void {
    if (!this.quill) return;

    // Detect if we should check for variables
    // Often triggered when '}' is typed
    const lastOp = delta.ops[delta.ops.length - 1];
    if (lastOp && lastOp.insert === '}') {
      const range = this.quill.getSelection();
      if (!range) return;

      const textBefore = this.quill.getText(0, range.index);
      const match = textBefore.match(
        /\{\{([a-zA-Z0-9_-]+)\}\}$|(\{\{([a-zA-Z0-9_-]+)\}\}$)/,
      );

      if (match) {
        const variableName = match[1] || match[3];
        const startIndex = range.index - (variableName.length + 4); // +4 for {{ }}

        // Remove the plain text and insert the blot
        this.quill.deleteText(startIndex, variableName.length + 4, 'user');
        this.quill.insertEmbed(startIndex, 'variable', variableName, 'user');
        this.quill.setSelection(startIndex + 1, 0, 'user');
      }
    }
  }

  private handleTextChange(): void {
    if (this.isInternalChange) return;

    const html = this.getHTMLContent();

    // Only emit if content actually changed
    if (html !== this.lastEmittedContent) {
      this.lastEmittedContent = html;
      this.onChange(html);
      this.contentChange.emit(html);
    }
  }

  public getHTMLContent(): string {
    if (!this.quill) return '';
    // Use root.innerHTML to ensure perfectly formed HTML is returned
    let html = this.quill.root.innerHTML;

    // Ensure variables are exported as plain text {{var}}
    // Quill might export <span> depending on implementation, but VariableBlot.value handles it if we use getContents
    // However, semantic HTML might keep the span. Let's fix it if needed.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.querySelectorAll('.ql-variable').forEach((el) => {
      const val = el.getAttribute('data-value');
      el.replaceWith(`{{${val}}}`);
    });
    html = tempDiv.innerHTML;

    return html === '<p><br></p>' ? '' : html;
  }

  private normalizeHTML(html: string): string {
    if (!html) return '';

    // Remove empty paragraphs and normalize whitespace
    return html
      .replace(/<p><br><\/p>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  toggleFullscreen(status: boolean): void {
    this.isFullscreen = status;

    const shell = this.editorShellRef?.nativeElement;
    if (!shell) return;

    if (status) {
      document.body.style.overflow = 'hidden';
      // Teleport to body to escape parent transforms
      this.renderer.appendChild(document.body, shell);
    } else {
      document.body.style.overflow = '';
      // Teleport back to host element
      this.renderer.appendChild(this.host.nativeElement, shell);
    }

    this.cdr.markForCheck();
  }

  private injectCustomToolbarButtons(): void {
    const toolbar = this.host.nativeElement.querySelector('.ql-toolbar');
    if (toolbar && this.toolbarTarget) {
      toolbar.appendChild(this.toolbarTarget.nativeElement);
      this.toolbarTarget.nativeElement.style.display = 'inline-flex';
    }
  }

  // ========================================
  // ControlValueAccessor Implementation
  // ========================================

  writeValue(value: string): void {
    if (!this.quill) return;

    const validValue = value || '';
    const currentHTML = this.getHTMLContent();

    // Check if it's markdown or HTML
    const isHTML = validValue.includes('<') && validValue.includes('>');
    let htmlContent = isHTML
      ? validValue
      : (this.mdService.parse(validValue) as string);

    // Normalize for comparison
    if (this.normalizeHTML(htmlContent) === this.normalizeHTML(currentHTML)) {
      return;
    }

    this.isInternalChange = true;

    // Save cursor position
    const selection = this.quill.getSelection();

    // Clear and set new content
    this.quill.setContents([]);

    if (htmlContent) {
      // Pre-process HTML to convert {{var}} into VariableBlots
      const processedHtml = htmlContent.replace(
        /\{\{([a-zA-Z0-9_-]+)\}\}/g,
        (match, name) => {
          return `<span class="ql-variable" data-value="${name}">{{${name}}}</span>`;
        },
      );
      this.quill.clipboard.dangerouslyPasteHTML(0, processedHtml);
    }

    // Restore cursor if editor was focused
    if (selection && this.isFocused) {
      const maxLength = this.quill.getLength();
      const safeIndex = Math.min(selection.index, maxLength - 1);
      this.quill.setSelection(safeIndex, 0);
    }

    // Update last emitted content
    this.lastEmittedContent = this.getHTMLContent();

    setTimeout(() => {
      this.isInternalChange = false;
    }, 0);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.quill) {
      this.quill.enable(!isDisabled);
    }
  }
}
